import React from "react";
import { StyleSheet, View } from "react-native";
import { router, Stack } from "expo-router";
import { SafeAreaView } from "react-native-safe-area-context";
import { Ionicons } from "@expo/vector-icons";
import { ThemedText } from "@/components/ThemedText";
import { ThemedButton } from "@/components/ThemedButton";
import { useThemeColor } from "@/hooks/useThemeColor";

/**
 * Screen displayed when the router navigates to an invalid or unknown route.
 *
 * @returns {React.JSX.Element} The rendered Not Found screen.
 */
export default function NotFoundScreen(): React.JSX.Element {
  const backgroundColor = useThemeColor({}, "background");
  const cardColor = useThemeColor({}, "card");
  const borderColor = useThemeColor({}, "border");
  const iconColor = useThemeColor({}, "icon");

  return (
    <SafeAreaView style={[styles.container, { backgroundColor }]}>
      <Stack.Screen options={{ title: "Not Found", headerShown: false }} />
      <View style={[styles.card, { backgroundColor: cardColor, borderColor }]}>
        <View style={styles.iconCircle}>
          <Ionicons name="compass-outline" size={56} color={iconColor} />
        </View>

        <ThemedText type="title" style={styles.title}>
          Screen Not Found
        </ThemedText>

        <ThemedText style={styles.description}>
          The page or memory you are looking for doesn't exist, has been moved, or you may not have permission to view it.
        </ThemedText>

        <View style={styles.actions}>
          <ThemedButton
            title="Go to Home"
            variant="primary"
            onPress={() => router.replace("/(tabs)")}
            style={styles.button}
          />
          {router.canGoBack() && (
            <ThemedButton
              title="Go Back"
              variant="secondary"
              onPress={() => router.back()}
              style={styles.button}
            />
          )}
        </View>
      </View>
    </SafeAreaView>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    alignItems: "center",
    justifyContent: "center",
    padding: 24,
  },
  card: {
    width: "100%",
    maxWidth: 420,
    borderRadius: 20,
    padding: 32,
    alignItems: "center",
    borderWidth: 1,
    shadowColor: "#000",
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.08,
    shadowRadius: 12,
    elevation: 3,
  },
  iconCircle: {
    width: 96,
    height: 96,
    borderRadius: 48,
    alignItems: "center",
    justifyContent: "center",
    marginBottom: 20,
    backgroundColor: "rgba(74, 96, 143, 0.1)",
  },
  title: {
    fontSize: 22,
    fontWeight: "700",
    textAlign: "center",
    marginBottom: 10,
  },
  description: {
    fontSize: 14,
    lineHeight: 20,
    textAlign: "center",
    opacity: 0.75,
    marginBottom: 28,
  },
  actions: {
    width: "100%",
    gap: 12,
  },
  button: {
    width: "100%",
  },
});
